package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import org.example.project.service.LizenzService
import org.example.project.settings.AppDropdown
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

private val STANDORT_GENERATOR_CONTROL_WIDTH = 150.px

internal fun eingebetteterStandortGeneratorRange(von: String, bis: String, pad: Int): List<String> {
    val start = von.toIntOrNull() ?: return emptyList()
    val end = bis.toIntOrNull() ?: start
    return (start..end).map { it.toString().padStart(pad, '0') }
}

private val infraFieldOrder = listOf("standort", "halle", "regal", "fach", "ebene", "tor", "bereich")
private const val MODULTYPEN_SCREEN_KEY = "PalettenTypVerwaltung"

internal fun infraCodeFieldOrder(): List<String> = infraFieldOrder

internal fun infraFieldLabel(field: String): String =
    when (field) {
        "standort" -> "Standort"
        "halle" -> "Halle"
        "regal" -> "Regal"
        "fach" -> "Fach"
        "ebene" -> "Ebene"
        "tor" -> "Tor"
        "bereich" -> "Bereich"
        else -> field
    }

internal fun modultypenFeatureAktiv(): Boolean =
    MODULTYPEN_SCREEN_KEY in LizenzService.getEffectiveScreens()

internal suspend fun loadModultypenFeatureAktiv(): Boolean {
    runCatching { LizenzService.loadKundenModuleState() }
    return modultypenFeatureAktiv()
}

private fun infraTypeMatches(row: All, key: String): Boolean {
    val normalizedKey = key.trim().lowercase().replace(" ", "_")
    val normalizedTyp = row.typ.trim().lowercase().replace(" ", "_")
    if (normalizedKey == "tor" && row.infraTorFormat.isNotBlank()) return true
    return row.id.trim().lowercase() == normalizedKey || normalizedTyp == normalizedKey
}

internal fun infraPlatzTypOptionen(infra: All?): List<All> =
    infra?.columnSettings.orEmpty()
        .filter {
            it.typ.trim().equals("Platz", ignoreCase = true) ||
                it.typ.trim().equals("WAPlatz", ignoreCase = true)
        }
        .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        .mapNotNull { row ->
            val key = row.id.trim()
            if (key.isBlank()) null else All(key = key, label = row.bezeichnung.ifBlank { "Platz $key" }, typ = row.typ)
        }
        .distinctBy { it.key }

internal fun infraModulTypDropdownAktiv(infra: All?, typ: String, modultypenAktiv: Boolean): Boolean {
    if (!modultypenAktiv) return false
    val meta = infra ?: return false
    val row = meta.columnSettings.firstOrNull { infraTypeMatches(it, typ) } ?: return false
    return row.modulTyp.equals("true", ignoreCase = true) || row.modulTyp == "✅"
}

private fun infraUses(row: All, field: String): Boolean =
    when (field) {
        "standort" -> row.standort.isNotBlank()
        "halle" -> row.halle.isNotBlank()
        "regal" -> row.typ.equals("Tor", ignoreCase = true).not() && row.regal.isNotBlank()
        "fach" -> row.typ.equals("Tor", ignoreCase = true).not() && row.fach.isNotBlank()
        "ebene" -> row.typ.equals("Tor", ignoreCase = true).not() && row.ebene.isNotBlank()
        "tor" -> row.infraTorFormat.isNotBlank()
        "bereich" -> row.typ.equals("Tor", ignoreCase = true).not() && row.bereich.isNotBlank()
        else -> false
    }

private fun infraFormat(infra: All, field: String): String =
    when (field) {
        "standort" -> infra.standort
        "halle" -> infra.halle
        "regal" -> infra.regal
        "fach" -> infra.fach
        "ebene" -> infra.ebene
        "tor" -> infra.infraTorFormat
        "bereich" -> infra.bereich
        else -> ""
    }

private fun infraCodePattern(infra: All, typ: String): String =
    infra.columnSettings.firstOrNull { infraTypeMatches(it, typ) }?.infraErzeugterCode.orEmpty()

internal fun infraAktiveFelder(infra: All?, typ: String): List<String> {
    val meta = infra ?: return emptyList()
    val row = meta.module.firstOrNull { infraTypeMatches(it, typ) } ?: return emptyList()
    return infraFieldOrder.filter { infraUses(row, it) }
}

private fun infraPad(value: String, format: String): String {
    val trimmed = value.trim()
    val numeric = trimmed.toIntOrNull()
    val normalized = numeric?.toString() ?: trimmed
    return if (format.isBlank()) normalized else normalized.padStart(format.length, '0')
}

internal fun infraCodeAusMeta(
    infra: All?,
    typ: String,
    values: Map<String, String>
): String {
    val meta = infra ?: return ""
    val fields = infraAktiveFelder(meta, typ).takeIf { it.isNotEmpty() } ?: return ""
    return fields
        .map { field -> infraPad(values[field].orEmpty(), infraFormat(meta, field)) }
        .joinToString("-")
}

internal fun infraAnzeigeCodeAusMeta(
    infra: All?,
    typ: String,
    values: Map<String, String>,
    visibleFields: Set<String>
): String {
    val meta = infra ?: return ""
    return infraAktiveFelder(meta, typ)
        .filter { it in visibleFields }
        .map { field -> infraPad(values[field].orEmpty(), infraFormat(meta, field)) }
        .filter { it.isNotBlank() }
        .joinToString("-")
}

@Composable
internal fun StandortGeneratorDropdown(
    label: String,
    selectedValue: String,
    options: List<String>,
    enabled: Boolean = true,
    onChange: (String) -> Unit
) {
    StandortGeneratorAllDropdown(
        label = label,
        selectedValue = selectedValue,
        options = options.map { All(key = it, label = it) },
        enabled = enabled,
        onChange = onChange
    )
}

@Composable
internal fun StandortGeneratorAllDropdown(
    label: String,
    selectedValue: String,
    options: List<All>,
    enabled: Boolean = true,
    onChange: (String) -> Unit
) {
    Div({
        style {
            width(STANDORT_GENERATOR_CONTROL_WIDTH)
            display(DisplayStyle.Block)
        }
    }) {
        AppDropdown(
            options = buildList {
                add(All(key = "", label = label))
                addAll(options)
            },
            selectedValue = selectedValue,
            enabled = enabled,
            onChange = onChange
        )
    }
}

@Composable
internal fun StandortGeneratorInput(
    label: String,
    value: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            width(STANDORT_GENERATOR_CONTROL_WIDTH)
            display(DisplayStyle.Block)
        }
    }) {
        Input(InputType.Text, attrs = {
            value(value)
            attr("placeholder", label)
            onInput { onChange(it.value) }
            style {
                width(100.percent)
                height(40.px)
                padding(0.px, 12.px)
                fontSize(13.px)
                color(org.jetbrains.compose.web.css.Color.white)
                backgroundColor(rgba(23, 26, 32, 0.38))
                border(1.px, LineStyle.Solid, org.jetbrains.compose.web.css.Color("rgba(255,255,255,0.12)"))
                borderRadius(8.px)
                outline("none")
                property("box-sizing", "border-box")
            }
        })
    }
}
